import 'es6-promise/auto';
import Vue from 'vue';
import Ionic from '@ionic/vue';
import DateFilter from './filters/date';
import App from './App.vue';
import router from './router.js';
import store from './store/';
import '@ionic/core/css/ionic.bundle.css';

// Import retry plugin
import retryPlugin from './plugins/retryPlugin';

import { initWebLowMemoryListener } from './utils/Lowmemorydetector.js';


// -------------------
// Helper: normalize any thrown value
// -------------------
function formatError(e) {
  if (!e) return { message: 'Unknown error', stack: 'No stack available' };
  if (typeof e === 'string') return { message: e, stack: 'No stack available' };
  if (e instanceof Error) return { message: e.message, stack: e.stack };
  try {
    return { message: JSON.stringify(e), stack: 'No stack available' };
  } catch {
    return { message: 'Unknown error', stack: 'No stack available' };
  }
}

// Disable Vue production tip
Vue.config.productionTip = false;

// Global Vue error handler
Vue.config.errorHandler = (err, vm, info) => {
  console.error('Vue Error:', err);
  console.error('Component:', vm);
  console.error('Info:', info);
};


// 🔧 DEV-ONLY low memory simulator (global)
if (process.env.NODE_ENV !== 'production') {
  window.__simulateLowMemory = () => {
    console.warn('🧪 Simulating low memory event');
    window.dispatchEvent(
      new CustomEvent('low-memory', {
        detail: { source: 'manual-test' },
      })
    );
  };
}


// -------------------
// Safe Capacitor plugin initialization
// -------------------
async function initPlugins() {
  if (!window.Capacitor?.Plugins) return;

  try {
    const { Network, SplashScreen, FirebaseMessaging } = window.Capacitor.Plugins;

    if (Network?.getStatus) {
      const status = await Network.getStatus();
      console.log('Network status:', status);
    }

    if (SplashScreen?.hide) {
      try {
        SplashScreen.hide();
        console.log('SplashScreen hidden');
      } catch (err) {
        console.warn('SplashScreen hide error:', err);
      }
    }

    if (FirebaseMessaging?.requestPermission) {
      try {
        await FirebaseMessaging.requestPermission();
        console.log('FirebaseMessaging permission requested');
      } catch (err) {
        console.warn('FirebaseMessaging init error (non-fatal):', err);
      }
    }

  } catch (err) {
    console.warn('Non-fatal plugin initialization error:', err);
  }
}

// -------------------
// Async-safe Vue bootstrap
// -------------------
async function bootstrapApp() {
  try {
    console.log('=== Starting App ===');

    // Install Ionic
    Vue.use(Ionic);

    // ✅ Install retry plugin BEFORE creating Vue instance
    console.log('📦 Installing retry plugin...');
    console.log('Plugin object:', retryPlugin);
    
    Vue.use(retryPlugin);
    
    // ✅ Verify plugin was installed
    console.log('✅ Plugin installed. Checking Vue.prototype...');
    console.log('Vue.prototype.$retryPopup:', Vue.prototype.$retryPopup);
    console.log('Vue.prototype.$retry:', typeof Vue.prototype.$retry);

    // Global date filter
    if (typeof DateFilter === 'function') {
      Vue.filter('date', DateFilter);
    } else {
      console.warn('DateFilter is not a function, fallback used');
      Vue.filter('date', v => v);
    }

    // Async component with safe fallback
    Vue.component('CustomBackButton', () =>
      import('./components/custom-back-button/index.vue')
        .then(module => module.default || module)
        .catch(err => {
          console.error('Failed to load CustomBackButton:', err);
          return { template: '<div style="color:red;">Back Button Load Failed</div>' };
        })
    );

    // Initialize plugins before mounting
    await initPlugins();

    console.log('Creating Vue instance...');
    const vueInstance = new Vue({
      router,
      store,
      render: h => h(App),
    }).$mount('#app');

    // ✅ Make Vue instance globally accessible for debugging
    window.__VUE_APP__ = vueInstance;
    
    console.log('✅ Vue instance created');
    console.log('✅ Testing $retryPopup on instance:', vueInstance.$retryPopup);
initWebLowMemoryListener();
    console.log('=== App Mounted Successfully ===');

  } catch (err) {
    const { message, stack } = formatError(err);
    console.error('❌ FATAL STARTUP ERROR:', err);

    document.body.innerHTML = `
      <div style="padding: 20px; background: white; font-family: Arial;">
        <h1 style="color: red;">App Startup Error</h1>
        <h3>${message}</h3>
        <pre style="background: #f5f5f5; padding: 10px; overflow: auto; font-size: 12px;">${stack}</pre>
      </div>
    `;
  }
}

// -------------------
// Run bootstrap
// -------------------
try {
  bootstrapApp();
} catch (err) {
  const { message, stack } = formatError(err);
  console.error('Fatal error outside bootstrapApp:', message, stack);
}